import { z } from 'zod';

/** What a notification is about; each can be switched off in Impostazioni. */
export const notificationKinds = [
  'deadlines',
  'events',
  'money',
  'focus',
  'budget',
  'streak',
] as const;
export type NotificationKind = (typeof notificationKinds)[number];

/** What a browser gets from its push service when it subscribes. */
export const webPushDeviceSchema = z.object({
  endpoint: z.url('Indirizzo di notifica non valido.').max(1000),
  keys: z.object({
    p256dh: z.string().min(1).max(200),
    auth: z.string().min(1).max(100),
  }),
});
export type WebPushDevice = z.infer<typeof webPushDeviceSchema>;

/** A phone with the app: Expo's address for it takes the place of the browser's. */
export const expoPushDeviceSchema = z.object({
  endpoint: z
    .string()
    .max(200)
    .regex(/^Expo(nent)?PushToken\[[^\]]+\]$/, 'Indirizzo di notifica non valido.'),
  expo: z.literal(true),
});
export type ExpoPushDevice = z.infer<typeof expoPushDeviceSchema>;

export const pushDeviceSchema = z.union([webPushDeviceSchema, expoPushDeviceSchema]);
export type PushDevice = z.infer<typeof pushDeviceSchema>;

export const removePushDeviceSchema = z.object({ endpoint: z.string().min(1).max(1000) });

/**
 * A notification the device planned: the server keeps it until `at` and sends it once per `key`.
 * The text is written on the device, which is where budgets and streaks are computed.
 */
export const plannedNotificationSchema = z.object({
  key: z.string().min(1).max(120),
  kind: z.enum(notificationKinds),
  at: z.iso.datetime('Data e ora non valide.'),
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().max(300),
  /** The page it opens. */
  url: z.string().startsWith('/').max(200),
});
export type PlannedNotification = z.infer<typeof plannedNotificationSchema>;

/**
 * "data" comes from your records and is the same on every device; "timer" is the focus timer of
 * the device in use. Each replaces only its own part, so a device cannot erase the other's timer.
 */
export const notificationScopes = ['data', 'timer'] as const;
export type NotificationScope = (typeof notificationScopes)[number];

export const notificationPlanSchema = z.object({
  scope: z.enum(notificationScopes),
  items: z.array(plannedNotificationSchema).max(100, 'Troppe notifiche in programma.'),
});
export type NotificationPlan = z.infer<typeof notificationPlanSchema>;

export const pushKeyResponseSchema = z.object({ publicKey: z.string() });
export type PushKeyResponse = z.infer<typeof pushKeyResponseSchema>;

export const pushTestResponseSchema = z.object({
  /** How many devices the push service accepted it for. */
  sent: z.number().int(),
});
export type PushTestResponse = z.infer<typeof pushTestResponseSchema>;
